import React from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaUser,
  FaClock,
  FaChevronLeft,
  FaShare,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaShieldAlt,
  FaExclamationTriangle,
  FaBalanceScale,
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DetailBlog8 = () => {
  const blogPost = {
    id: 5,
    title: "Redressement fiscal : comprendre, anticiper et se défendre",
    excerpt:
      "Le terme 'redressement fiscal' est l'une des expressions les plus redoutées par tout dirigeant d'entreprise. Découvrez comment démystifier cette procédure, comprendre vos droits et protéger votre entreprise avec l'accompagnement de votre expert-comptable.",
    content: `
      <div class="intro-block">
        <p class="opening-text">Le terme "redressement fiscal" est sans doute l'une des expressions les plus redoutées par tout dirigeant d'entreprise. Souvent perçu comme une sanction, il s'agit en réalité d'une procédure administrative qui, bien que sérieuse, peut être comprise, gérée et même contestée.</p>
        
        <div class="mission-statement">
          <p>Les experts-comptables accompagnent quotidiennement les entreprises dans le pilotage de leurs obligations. Notre objectif avec ce guide est de démystifier le redressement fiscal, de vous en expliquer les étapes clés conformément au Code Général des Impôts (CGI) du Bénin, et surtout, de vous donner les clés pour l'anticiper et y faire face sereinement.</p>
        </div>
      </div>

      <div class="numbered-section">
        <div class="section-number">01</div>
        <h2 class="main-heading">Contrôle Fiscal vs Redressement Fiscal : clarifions les concepts</h2>
        
        <p class="section-intro">Il est essentiel de ne pas confondre ces deux notions, car l'une est la cause et l'autre, la conséquence potentielle.</p>

        <div class="comparison-grid">
          <div class="comparison-card audit-card">
            <div class="card-icon-header">
              <span class="icon-circle"></span>
              <h3>Le Contrôle Fiscal</h3>
            </div>
            <p>C'est l'action de vérification initiée par l'Administration des impôts. Un agent (le vérificateur) examine la comptabilité et les déclarations de votre entreprise pour s'assurer de leur cohérence, de leur sincérité et de leur conformité avec la législation en vigueur.</p>
            <div class="info-badge">Un contrôle n'aboutit pas toujours à une sanction</div>
          </div>

          <div class="comparison-card correction-card">
            <div class="card-icon-header">
              <span class="icon-circle">📝</span>
              <h3>Le Redressement Fiscal</h3>
            </div>
            <p>Si, à l'issue du contrôle, le vérificateur constate des insuffisances, inexactitudes, omissions ou dissimulations, il engage une procédure de redressement. Il vous envoie alors une "proposition de rectification".</p>
            <div class="warning-badge">C'est cet acte qui formalise la correction</div>
          </div>
        </div>

        <div class="summary-box">
          <strong>En résumé :</strong> Le contrôle fiscal est l'audit, et le redressement fiscal est la correction qui peut en découler.
        </div>
      </div>

      <div class="numbered-section">
        <div class="section-number">02</div>
        <h2 class="main-heading">Le Déroulement de la Procédure : Les Étapes Clés au Bénin</h2>
        
        <p class="section-intro">Un redressement fiscal suit un processus contradictoire strict, qui garantit à l'entreprise le droit de se défendre.</p>

        <div class="timeline-container">
          <div class="timeline-item">
            <div class="timeline-marker">
              <span class="marker-number">1</span>
            </div>
            <div class="timeline-content">
              <h4>L'Avis de vérification et le contrôle sur place ou à distance</h4>
              <p>Tout commence par la réception d'un <strong>avis de vérification</strong> qui vous informe du début du contrôle, de la période vérifiée et des impôts concernés (TVA, Impôt sur les Sociétés, etc.). Le vérificateur interviendra ensuite dans vos locaux ou vous demandera de lui transmettre les pièces nécessaires, notamment le Fichier des Écritures Comptables (FEC).</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-marker">
              <span class="marker-number">2</span>
            </div>
            <div class="timeline-content">
              <h4>La Proposition de rectification</h4>
              <p>À la fin de ses investigations, si des anomalies sont relevées, l'administration vous adresse une <strong>proposition de rectification</strong> par courrier recommandé. Ce document est crucial : il doit détailler précisément les motifs des corrections, leur montant, et la méthode de calcul.</p>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-marker">
              <span class="marker-number">3</span>
            </div>
            <div class="timeline-content">
              <h4>Votre droit de déponse (Délai de 30 Jours)</h4>
              <p>À compter de la réception de cette proposition, vous disposez d'un <strong>délai de 30 jours</strong> pour répondre. Deux options s'offrent à vous :</p>
              <ul class="options-list">
                <li>Accepter le redressement (totalement ou partiellement).</li>
                <li>Contester le redressement en formulant des observations écrites, argumentées et justifiées.</li>
              </ul>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-marker">
              <span class="marker-number">4</span>
            </div>
            <div class="timeline-content">
              <h4>Confirmation de l'Administration</h4>
              <p>Si vous contestez, l'administration a <strong>deux (2) mois</strong> pour répondre à vos observations et confirmer les redressements qu'elle maintient. Un silence de sa part dans ce délai vaut acceptation de vos arguments.</p>
            </div>
          </div>
        </div>

        <div class="expert-warning-box">
          <div class="warning-icon">⚠️</div>
          <div class="warning-content">
            <h5>Le conseil de l'expert</h5>
            <p>Le silence de votre part vaut acceptation ! Il est donc impératif de réagir dans ce délai, même pour demander des informations complémentaires. C'est à ce stade que l'intervention de votre expert-comptable est la plus stratégique.</p>
          </div>
        </div>
      </div>

      <div class="numbered-section">
        <div class="section-number">03</div>
        <h2 class="main-heading">Les Conséquences d'un Redressement : Un Impact Financier et Réputationnel</h2>
        
        <p class="section-intro">Les répercussions d'un redressement peuvent être lourdes et ne se limitent pas au paiement de l'impôt éludé.</p>

        <div class="consequences-grid">
          <div class="consequence-item financial">
            <div class="consequence-header">
              <span class="consequence-icon">💰</span>
              <h4>Impositions Supplémentaires</h4>
            </div>
            <p>C'est le principal dû, correspondant à l'impôt qui aurait dû être payé.</p>
          </div>

          <div class="consequence-item interest">
            <div class="consequence-header">
              <span class="consequence-icon">⏱️</span>
              <h4>Intérêts de Retard</h4>
            </div>
            <p>Calculés sur le montant de l'impôt supplémentaire, ils visent à compenser le Trésor public pour le retard de paiement.</p>
          </div>

          <div class="consequence-item penalties">
            <div class="consequence-header">
              <span class="consequence-icon"></span>
              <h4>Pénalités et Majorations</h4>
            </div>
            <p>Leur taux varie selon la gravité de la faute retenue par l'Administration :</p>
            <ul class="penalty-list">
              <li><strong>Manquement délibéré :</strong> Majoration de 40 %</li>
              <li><strong>Manœuvres frauduleuses :</strong> Majoration pouvant atteindre 80 %</li>
            </ul>
          </div>

          <div class="consequence-item reputation">
            <div class="consequence-header">
              <span class="consequence-icon"></span>
              <h4>Impact sur la Réputation</h4>
            </div>
            <p>Un redressement fiscal, surtout s'il est important, peut entacher la crédibilité de votre entreprise auprès des banques, des investisseurs et des partenaires commerciaux.</p>
          </div>

          <div class="consequence-item criminal">
            <div class="consequence-header">
              <span class="consequence-icon"></span>
              <h4>Sanctions Pénales</h4>
            </div>
            <p>Dans les cas de fraude fiscale avérée, la responsabilité personnelle du dirigeant peut être engagée, avec des risques d'amendes lourdes et de peines d'emprisonnement.</p>
          </div>
        </div>
      </div>

      <div class="numbered-section">
        <div class="section-number">04</div>
        <h2 class="main-heading">Que faire en cas de désaccord ?</h2>

        <div class="recourse-pathway">
          <div class="pathway-step step-1">
            <div class="step-header">
              <span class="step-badge">Étape 1</span>
              <h3>La Saisine de la Commission des Impôts</h3>
            </div>
            <p class="step-description">Si le désaccord persiste sur des questions de fait (par exemple, le caractère normal d'un acte de gestion), vous pouvez saisir la Commission des Impôts.</p>
            
            <div class="details-box">
              <div class="detail-item">
                <span class="detail-label">Délai de Saisine :</span>
                <span class="detail-value">La demande doit être faite dans les <strong>cinq (5) jours</strong> suivant la réception de la confirmation de rectification de l'administration.</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Décision de la Commission :</span>
                <span class="detail-value">La commission rend un avis dans un délai de <strong>trente (30) jours</strong>. Cet avis ne lie ni le contribuable ni l'administration, mais la partie qui ne s'y conforme pas supportera la charge de la preuve en cas de recours ultérieur devant le juge.</span>
              </div>
            </div>
          </div>

          <div class="pathway-step step-2">
            <div class="step-header">
              <span class="step-badge">Étape 2</span>
              <h3>La Réclamation Contentieuse Préalable</h3>
            </div>
            <p class="step-description">Une fois l'imposition mise en recouvrement (via un avis de mise en recouvrement), la phase contentieuse formelle débute.</p>
            
            <div class="details-box">
              <div class="detail-item">
                <span class="detail-label">Dépôt de la Réclamation :</span>
                <span class="detail-value">Vous devez adresser une réclamation préalable au Ministre chargé des Finances.</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Délai :</span>
                <span class="detail-value">Cette réclamation doit être déposée dans un délai de <strong>trois (3) mois</strong> à compter de la date de notification de l'avis de mise en recouvrement.</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Conditions de forme :</span>
                <span class="detail-value">Pour être recevable, la réclamation doit obligatoirement mentionner les impôts contestés, être accompagnée du titre de perception, contenir un exposé des motifs et vos conclusions, et être signée.</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Décision de l'Administration :</span>
                <span class="detail-value">L'administration dispose d'un délai de <strong>deux (2) mois</strong> pour statuer sur votre réclamation. L'absence de réponse dans ce délai vaut décision de rejet.</span>
              </div>
            </div>

            <div class="sursis-box">
              <h5>💡 Sursis de Paiement</h5>
              <p>L'introduction d'une réclamation ne suspend pas l'obligation de payer. Cependant, vous pouvez demander un sursis de paiement pour la partie contestée de l'impôt à condition de :</p>
              <ul>
                <li>Préciser le montant du dégrèvement demandé.</li>
                <li>Avoir payé la partie non contestée de l'impôt.</li>
                <li>Constituer une garantie de 25% du montant contesté sous forme de paiement au trésor public.</li>
              </ul>
            </div>
          </div>

          <div class="pathway-step step-3">
            <div class="step-header">
              <span class="step-badge">Étape 3</span>
              <h3>Le Recours juridictionnel</h3>
            </div>
            <p class="step-description">En cas de rejet de votre réclamation (explicite ou tacite), la dernière étape est le recours devant la justice.</p>
            
            <div class="details-box">
              <div class="detail-item">
                <span class="detail-label">Saisine du tribunal :</span>
                <span class="detail-value">Le litige peut être porté devant le tribunal de première instance statuant en matière administrative.</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Délai :</span>
                <span class="detail-value">Vous disposez d'un délai de <strong>deux (2) mois</strong> pour saisir le tribunal. Ce délai court à partir de la date de notification de la décision de rejet de votre réclamation ou de l'expiration du délai de réponse de deux mois de l'administration.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="numbered-section">
        <div class="section-number">05</div>
        <h2 class="main-heading">La Prévention : votre meilleur bouclier</h2>
        
        <div class="philosophy-box">
          <p>Chez <strong>CINEF-EC</strong>, notre conviction est simple : la meilleure façon de gérer un redressement fiscal est de l'éviter. La conformité fiscale n'est pas une contrainte, c'est une assurance pour la pérennité de votre entreprise.</p>
        </div>

        <h4 class="pillars-title">Voici les piliers d'une prévention efficace :</h4>

        <div class="pillars-grid">
          <div class="pillar-card">
            <div class="pillar-icon"></div>
            <h5>Tenue Rigoureuse</h5>
            <p>Une tenue rigoureuse de la comptabilité et un archivage probant de toutes les pièces justificatives.</p>
          </div>

          <div class="pillar-card">
            <div class="pillar-icon"></div>
            <h5>Déclarations Précises</h5>
            <p>Des déclarations fiscales précises, complètes et déposées dans les délais.</p>
          </div>

          <div class="pillar-card">
            <div class="pillar-icon"></div>
            <h5>Contrôles Internes</h5>
            <p>La mise en place de contrôles internes pour valider la fiabilité des informations financières et fiscales.</p>
          </div>

          <div class="pillar-card">
            <div class="pillar-icon"></div>
            <h5>Consultation Régulière</h5>
            <p>Une consultation régulière avec votre expert-comptable pour anticiper les risques et optimiser votre fiscalité en toute légalité.</p>
          </div>
        </div>
      </div>

      <div class="role-section">
        <h3 class="role-title">Rôle de votre Expert-comptable à Vos Côtés</h3>
        
        <p class="role-intro">Un redressement fiscal est une épreuve technique et psychologique pour un dirigeant. Notre rôle est de vous assister à chaque étape :</p>

        <div class="assistance-timeline">
          <div class="assistance-phase">
            <div class="phase-label">En amont</div>
            <p>En sécurisant votre organisation comptable et fiscale.</p>
          </div>

          <div class="assistance-phase">
            <div class="phase-label">Pendant le contrôle</div>
            <p>En préparant les documents, en assistant le vérificateur et en défendant vos choix de gestion.</p>
          </div>

          <div class="assistance-phase">
            <div class="phase-label">Après la notification</div>
            <p>En analysant la proposition de rectification, en rédigeant une contestation argumentée et en négociant avec l'Administration pour défendre au mieux vos intérêts.</p>
          </div>
        </div>
      </div>

      <div class="final-cta-section">
        <h3>Ne laissez pas une question fiscale devenir une menace pour votre activité</h3>
        <p class="cta-subtitle">La clé est l'anticipation.</p>
        <p>Contactez votre expert-comptable pour un diagnostic fiscal préventif ou pour vous assister face à l'Administration. Notre mission est de protéger votre entreprise et de sécuriser votre croissance.</p>
      </div>

      <div class="resources-footer">
        <h4> Pour aller plus loin</h4>
        <ul>
          <li><a href="https://www.impots.bj/" target="_blank">Site de la Direction Générale des Impôts du Bénin</a></li>
          <li><a href="https://oeccabenin.bj/" target="_blank">Site de l'Ordre des Experts-comptables</a></li>
        </ul>
      </div>
    `,
    image: "/assets/images/redressement-fiscal-cover.jpg",
    categories: ["Fiscalité", "Conformité", "Gestion des Risques"],
    tags: [
      "RedressementFiscal",
      "ContrôleFiscal",
      "CGI",
      "Conformité",
      "GestionDesRisques",
      "ExpertComptable",
      "PME",
      "Bénin",
    ],
    date: "10 octobre 2025",
    author: "Wilfrid DEDJI",
    authorTitle: "Expert Comptable",
    readTime: "12 min",
    featured: true,
  };

  const similarBlogs = [
    {
      id: 1,
      title: "Guide complet de la conformité fiscale pour PME",
      image: "/assets/images/image1.png",
      date: "8 octobre 2025",
      category: "Fiscalité",
    },
    {
      id: 3,
      title: "Les obligations déclaratives 2025 au Bénin",
      image: "/assets/images/image8.png",
      date: "1 octobre 2025",
      category: "Conformité",
    },
  ];

  return (
    <>
      <Header />
      <div className="blog-wrapper">
        <div className="container">
          <nav className="breadcrumb-custom">
            <Link to="/" className="crumb-link">Accueil</Link>
            <span className="crumb-divider">•</span>
            <Link to="/actualites/blog" className="crumb-link">Blog</Link>
            <span className="crumb-divider">•</span>
            <span className="crumb-active">Redressement Fiscal</span>
          </nav>

          <div className="row">
            <div className="col-lg-8">
              <article className="main-article">
                <header className="article-top">
                  <div className="badges-row">
                    {blogPost.categories.map((category, index) => (
                      <span key={index} className="badge-category">
                        {category}
                      </span>
                    ))}
                  </div>
                  
                  <h1 className="article-main-title">{blogPost.title}</h1>

                  <div className="meta-info-row">
                    <div className="meta-info-item">
                      <FaCalendarAlt className="meta-icon-custom" />
                      <span>{blogPost.date}</span>
                    </div>
                    <div className="meta-info-item">
                      <FaUser className="meta-icon-custom" />
                      <span>{blogPost.author}</span>
                    </div>
                    <div className="meta-info-item">
                      <FaClock className="meta-icon-custom" />
                      <span>{blogPost.readTime} de lecture</span>
                    </div>
                  </div>
                </header>

                

                <div className="excerpt-container">
                  <div className="excerpt-marker">
                    <FaShieldAlt />
                  </div>
                  <p className="excerpt-text">{blogPost.excerpt}</p>
                </div>

                <div
                  className="content-body"
                  dangerouslySetInnerHTML={{ __html: blogPost.content }}
                />

                <footer className="article-bottom">
                  <div className="tags-area">
                    <span className="tags-title">Tags :</span>
                    <div className="tags-cloud">
                      {blogPost.tags.map((tag, index) => (
                        <Link key={index} to="#" className="tag-link">
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="share-area">
                    <h5 className="share-title-custom">
                      <FaShare className="me-2" />
                      Partager l'article
                    </h5>
                    <div className="social-buttons-row">
                      <a href="#" className="social-btn-custom linkedin" title="LinkedIn">
                        <FaLinkedin />
                      </a>
                      <a href="#" className="social-btn-custom twitter" title="Twitter">
                        <FaTwitter />
                      </a>
                      <a href="#" className="social-btn-custom facebook" title="Facebook">
                        <FaFacebook />
                      </a>
                    </div>
                  </div>

                  <div className="nav-area">
                    <Link to="/actualites/blog" className="back-btn-custom">
                      <FaChevronLeft />
                      <span>Retour au blog</span>
                    </Link>
                  </div>
                </footer>
              </article>
            </div>

            <aside className="col-lg-4">
              <div className="widget-card author-widget">
                <div className="widget-top">
                  <FaUser className="widget-icon" />
                  <h4>À propos de l'auteur</h4>
                </div>
                <div className="author-details">
                  <div className="author-img-wrapper">
                    <img
                      src="../assets/images/image5.png"
                      alt={blogPost.author}
                    />
                  </div>
                  <h5 className="author-name-custom">{blogPost.author}</h5>
                  <p className="author-position">{blogPost.authorTitle}</p>
                  <p className="author-bio-text">
                    Cabinet d'expertise comptable certifié OECCA, spécialisé dans l'accompagnement fiscal et la gestion des risques pour les PME béninoises. Expertise reconnue en procédures de redressement et contentieux fiscal.
                  </p>
                </div>
              </div>

              <div className="widget-card similar-widget">
                <div className="widget-top">
                  <FaExclamationTriangle className="widget-icon" />
                  <h4>Articles connexes</h4>
                </div>
                <div className="similar-list">
                  {similarBlogs.map((blog) => (
                    <Link
                      key={blog.id}
                      to={`/blog/${blog.id}`}
                      className="similar-card"
                    >
                      <div className="similar-img-box">
                        <img src={blog.image} alt={blog.title} />
                        <span className="similar-category">{blog.category}</span>
                      </div>
                      <div className="similar-text-box">
                        <h6 className="similar-title-small">{blog.title}</h6>
                        <div className="similar-date-small">
                          <FaCalendarAlt />
                          <span>{blog.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="widget-card newsletter-widget">
                <div className="widget-top">
                  <FaBalanceScale className="widget-icon" />
                  <h4>Alerte Fiscale</h4>
                </div>
                <p className="newsletter-description">
                  Restez informé des évolutions du CGI, des nouvelles procédures et de nos conseils pour sécuriser votre conformité fiscale.
                </p>
                <form className="form-newsletter">
                  <input
                    type="email"
                    className="input-newsletter"
                    placeholder="Votre email professionnel"
                    required
                  />
                  <button type="submit" className="btn-newsletter">
                    S'inscrire
                  </button>
                </form>
              </div>

              <div className="widget-card resources-widget">
                <div className="widget-top">
                  <FaShieldAlt className="widget-icon" />
                  <h4>Liens Utiles</h4>
                </div>
                <ul className="links-list-custom">
                  <li>
                    <a href="https://www.impots.bj/" target="_blank" rel="noopener noreferrer">
                      Direction Générale des Impôts
                      <span className="arrow-link">→</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://oeccabenin.bj/" target="_blank" rel="noopener noreferrer">
                      Ordre des Experts-Comptables
                      <span className="arrow-link">→</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" target="_blank" rel="noopener noreferrer">
                      Code Général des Impôts
                      <span className="arrow-link">→</span>
                    </a>
                  </li>
                  <li>
                    <Link to="/services/comptabilite-fiscalite">
                      Nos Services
                      <span className="arrow-link">→</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@400;500;600;700;800;900&display=swap");

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .blog-wrapper {
          background: #ffffff;
          padding: 3.5rem 0 6rem;
          font-family: "Inter", sans-serif;
          color: #1e293b;
        }

        /* Breadcrumb */
        .breadcrumb-custom {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem 0;
          font-size: 0.9rem;
          margin-bottom: 2rem;
        }

        .crumb-link {
          color: #64748b;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .crumb-link:hover {
          color: #dc3545;
        }

        .crumb-divider {
          color: #cbd5e1;
          font-weight: 700;
        }

        .crumb-active {
          color: #1e293b;
          font-weight: 600;
        }

        /* Article */
        .main-article {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .article-top {
          padding: 3rem 3rem 2rem;
        }

        .badges-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .badge-category {
          background: linear-gradient(135deg, #dc3545, #c82333);
          color: white;
          padding: 0.6rem 1.5rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }

        .article-main-title {
          font-family: "Poppins", sans-serif;
          font-size: 3.2rem;
          font-weight: 900;
          line-height: 1.1;
          color: #0f172a;
          margin-bottom: 2rem;
          letter-spacing: -1px;
        }

        .meta-info-row {
          display: flex;
          flex-wrap: wrap;
          gap: 2.5rem;
          padding-top: 1.75rem;
          border-top: 3px solid #f1f5f9;
        }

        .meta-info-item {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          color: #64748b;
          font-size: 0.95rem;
          font-weight: 600;
        }

        .meta-icon-custom {
          color: #dc3545;
          font-size: 1.1rem;
        }

        /* Hero Image */
        .hero-image-container {
          position: relative;
          width: 100%;
          height: 500px;
          overflow: hidden;
        }

        .hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hero-gradient {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60%;
          background: linear-gradient(to top, rgba(15, 23, 42, 0.7), transparent);
        }

        /* Excerpt */
        .excerpt-container {
          padding: 3rem;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.08), rgba(25, 135, 84, 0.08));
          display: flex;
          gap: 2rem;
          align-items: center;
          border-left: 6px solid #dc3545;
        }

        .excerpt-marker {
          flex-shrink: 0;
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #dc3545, #198754);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.8rem;
          box-shadow: 0 8px 24px rgba(220, 53, 69, 0.3);
        }

        .excerpt-text {
          font-size: 1.25rem;
          line-height: 1.7;
          color: #334155;
          font-weight: 500;
          margin: 0;
        }

        /* Content Body */
        .content-body {
          padding: 3rem;
          font-size: 1.05rem;
          line-height: 1.85;
          color: #475569;
        }

        .intro-block {
          margin-bottom: 3.5rem;
        }

        .opening-text {
          font-size: 1.2rem;
          line-height: 1.8;
          color: #334155;
          margin-bottom: 2rem;
        }

        .mission-statement {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          padding: 2rem;
          border-radius: 15px;
          border-left: 5px solid #198754;
        }

        .mission-statement p {
          margin: 0;
          font-weight: 500;
          color: #1e293b;
        }

        /* Numbered Sections */
        .numbered-section {
          position: relative;
          margin: 4rem 0;
          padding-left: 5rem;
        }

        .section-number {
          position: absolute;
          left: -30px;
          top: 0;
          font-family: "Poppins", sans-serif;
          font-size: 5rem;
          font-weight: 900;
          color: #427bb4ff;
          line-height: 1;
          letter-spacing: -2px;
        }

        .main-heading {
          font-family: "Poppins", sans-serif;
          font-size: 2.2rem;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-intro {
          font-size: 1.15rem;
          color: #64748b;
          margin-bottom: 2.5rem;
          font-style: italic;
        }

        /* Comparison Grid */
        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin: 2.5rem 0;
        }

        .comparison-card {
          padding: 2.5rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .comparison-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        .audit-card {
          background: linear-gradient(135deg, #dbeafe, #f0f9ff);
          border: 2px solid #3b82f6;
        }

        .correction-card {
          background: linear-gradient(135deg, #fee2e2, #fef2f2);
          border: 2px solid #dc3545;
        }

        .card-icon-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .icon-circle {
          font-size: 2.5rem;
        }

        .card-icon-header h3 {
          font-size: 1.4rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .comparison-card p {
          color: #475569;
          line-height: 1.7;
          margin-bottom: 1.5rem;
        }

        .info-badge {
          background: #3b82f6;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          display: inline-block;
        }

        .warning-badge {
          background: #dc3545;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          display: inline-block;
        }

        .summary-box {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          border-left: 4px solid #198754;
          margin-top: 2rem;
          font-size: 1.1rem;
          color: #1e293b;
        }

        /* Timeline */
        .timeline-container {
          margin: 3rem 0;
          position: relative;
        }

        .timeline-container::before {
          content: "";
          position: absolute;
          left: 25px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(to bottom, #dc3545, #198754);
        }

        .timeline-item {
          position: relative;
          padding-left: 80px;
          margin-bottom: 3rem;
        }

        .timeline-marker {
          position: absolute;
          left: 0;
          top: 0;
        }

        .marker-number {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          font-size: 1.5rem;
          box-shadow: 0 4px 16px rgba(220, 53, 69, 0.4);
        }

        .timeline-content {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          border: 2px solid #f1f5f9;
        }

        .timeline-content h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 1rem;
        }

        .timeline-content p {
          color: #475569;
          margin-bottom: 1rem;
        }

        .options-list {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0 0;
        }

        .options-list li {
          padding: 0.75rem 1rem;
          background: #f8fafc;
          border-radius: 8px;
          margin-bottom: 0.75rem;
          border-left: 3px solid #198754;
        }

        /* Expert Warning */
        .expert-warning-box {
          display: flex;
          gap: 1.5rem;
          background: linear-gradient(135deg, #fff7ed, #fef3c7);
          padding: 2rem;
          border-radius: 15px;
          border: 2px solid #f59e0b;
          margin-top: 2.5rem;
        }

        .warning-icon {
          font-size: 2.5rem;
          flex-shrink: 0;
        }

        .warning-content h5 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #92400e;
          margin-bottom: 0.75rem;
        }

        .warning-content p {
          color: #78350f;
          margin: 0;
          font-weight: 500;
        }

        /* Consequences Grid */
        .consequences-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin: 2.5rem 0;
        }

        .consequence-item {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          border: 2px solid #f1f5f9;
          transition: all 0.3s ease;
        }

        .consequence-item:hover {
          border-color: #dc3545;
          transform: translateY(-5px);
          box-shadow: 0 8px 24px rgba(220, 53, 69, 0.2);
        }

        .consequence-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .consequence-icon {
          font-size: 2rem;
        }

        .consequence-header h4 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .consequence-item p {
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        .penalty-list {
          list-style: none;
          padding: 0;
          margin: 1rem 0 0;
        }

        .penalty-list li {
          padding: 0.5rem 0;
          color: #475569;
        }

        /* Recourse Pathway */
        .recourse-pathway {
          margin: 3rem 0;
        }

        .pathway-step {
          background: white;
          border-radius: 15px;
          padding: 2.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border-left: 5px solid;
        }

        .step-1 {
          border-left-color: #3b82f6;
        }

        .step-2 {
          border-left-color: #f59e0b;
        }

        .step-3 {
          border-left-color: #dc3545;
        }

        .step-header {
          margin-bottom: 1.5rem;
        }

        .step-badge {
          display: inline-block;
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .step-header h3 {
          font-size: 1.6rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .step-description {
          color: #475569;
          font-size: 1.05rem;
          margin-bottom: 2rem;
        }

        .details-box {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 12px;
          margin: 1.5rem 0;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 700;
          color: #1e293b;
          font-size: 0.95rem;
        }

        .detail-value {
          color: #64748b;
          line-height: 1.6;
        }

        .sursis-box {
          background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
          padding: 2rem;
          border-radius: 12px;
          border: 2px solid #0ea5e9;
          margin-top: 2rem;
        }

        .sursis-box h5 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0c4a6e;
          margin-bottom: 1rem;
        }

        .sursis-box ul {
          list-style: none;
          padding: 0;
          margin: 1rem 0 0;
        }

        .sursis-box li {
          padding: 0.5rem 0 0.5rem 1.5rem;
          position: relative;
          color: #0c4a6e;
        }

        .sursis-box li::before {
          content: "→";
          position: absolute;
          left: 0;
          color: #0ea5e9;
          font-weight: 700;
        }

        /* Philosophy */
        .philosophy-box {
          background: linear-gradient(135deg, #dcfce7, #f0fdf4);
          padding: 2.5rem;
          border-radius: 15px;
          border: 2px solid #198754;
          margin-bottom: 2.5rem;
          text-align: center;
        }

        .philosophy-box p {
          font-size: 1.2rem;
          color: #14532d;
          margin: 0;
          font-weight: 500;
        }

        .pillars-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2rem;
          text-align: center;
        }

        /* Pillars Grid */
        .pillars-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 2rem;
          margin: 2rem 0;
        }

        .pillar-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 2px solid #f1f5f9;
          transition: all 0.3s ease;
        }

        .pillar-card:hover {
          border-color: #198754;
          transform: translateY(-8px);
          box-shadow: 0 12px 32px rgba(25, 135, 84, 0.2);
        }

        .pillar-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .pillar-card h5 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 0.75rem;
        }

        .pillar-card p {
          color: #64748b;
          font-size: 0.95rem;
          margin: 0;
        }

        /* Role Section */
        .role-section {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          padding: 3rem;
          border-radius: 20px;
          margin: 3rem 0;
        }

        .role-title {
          font-size: 1.8rem;
          font-weight: 800;
          color: #0f172a;
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .role-intro {
          text-align: center;
          color: #475569;
          font-size: 1.1rem;
          margin-bottom: 2.5rem;
        }

        .assistance-timeline {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
        }

        .assistance-phase {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }

        .phase-label {
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          padding: 0.5rem 1.25rem;
          border-radius: 25px;
          font-size: 0.9rem;
          font-weight: 700;
          display: inline-block;
          margin-bottom: 1rem;
        }

        .assistance-phase p {
          color: #475569;
          margin: 0;
        }

        /* Final CTA */
        .final-cta-section {
          background: linear-gradient(135deg, #dc3545, #c82333);
          color: white;
          padding: 4rem;
          border-radius: 20px;
          text-align: center;
          margin: 4rem 0 3rem;
          box-shadow: 0 12px 40px rgba(220, 53, 69, 0.4);
        }

        .final-cta-section h3 {
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 1rem;
          color: white;
        }

        .cta-subtitle {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #fff;
          opacity: 0.95;
        }

        .final-cta-section p {
          font-size: 1.1rem;
          margin: 0;
          color: white;
          opacity: 0.9;
        }

        /* Resources Footer */
        .resources-footer {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 12px;
          margin-top: 2rem;
        }

        .resources-footer h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 1rem;
        }

        .resources-footer ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .resources-footer li {
          padding: 0.5rem 0;
        }

        .resources-footer a {
          color: #198754;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
        }

        .resources-footer a:hover {
          color: #dc3545;
        }

        /* Article Footer */
        .article-bottom {
          padding: 3rem;
          border-top: 3px solid #f1f5f9;
        }

        .tags-area {
          margin-bottom: 3rem;
        }

        .tags-title {
          font-weight: 700;
          color: #1e293b;
          font-size: 1rem;
          margin-bottom: 1rem;
          display: block;
        }

        .tags-cloud {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .tag-link {
          background: #f1f5f9;
          color: #475569;
          padding: 0.5rem 1.25rem;
          border-radius: 25px;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .tag-link:hover {
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          border-color: #dc3545;
          transform: translateY(-3px);
        }

        .share-area {
          padding: 2.5rem 0;
          border-top: 2px solid #f1f5f9;
          border-bottom: 2px solid #f1f5f9;
          margin-bottom: 2.5rem;
        }

        .share-title-custom {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
        }

        .social-buttons-row {
          display: flex;
          gap: 1.25rem;
        }

        .social-btn-custom {
          width: 55px;
          height: 55px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          text-decoration: none;
          font-size: 1.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .social-btn-custom:hover {
          transform: translateY(-6px) scale(1.05);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
        }

        .linkedin {
          background: linear-gradient(135deg, #0077b5, #005582);
        }

        .twitter {
          background: linear-gradient(135deg, #1da1f2, #0d8bd9);
        }

        .facebook {
          background: linear-gradient(135deg, #4267b2, #2d4373);
        }

        .nav-area {
          text-align: center;
        }

        .back-btn-custom {
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          background: white;
          color: #475569;
          padding: 1.25rem 2.5rem;
          border-radius: 35px;
          text-decoration: none;
          border: 3px solid #e2e8f0;
          font-weight: 700;
          font-size: 1.05rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .back-btn-custom:hover {
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          border-color: #dc3545;
          transform: translateX(-10px);
          box-shadow: 0 8px 24px rgba(220, 53, 69, 0.3);
        }

        /* Sidebar */
        .widget-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem;
          margin-bottom: 2.5rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
          border: 2px solid #f1f5f9;
        }

        .widget-top {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.25rem;
          border-bottom: 3px solid #f8fafc;
        }

        .widget-icon {
          color: #dc3545;
          font-size: 1.8rem;
        }

        .widget-top h4 {
          font-size: 1.4rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .author-details {
          text-align: center;
        }

        .author-img-wrapper {
          width: 110px;
          height: 110px;
          border-radius: 50%;
          overflow: hidden;
          margin: 0 auto 1.5rem;
          border: 4px solid #f1f5f9;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .author-img-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .author-name-custom {
          font-size: 1.3rem;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 0.5rem;
        }

        .author-position {
          font-size: 1rem;
          color: #dc3545;
          font-weight: 700;
          margin-bottom: 1.25rem;
        }

        .author-bio-text {
          font-size: 0.95rem;
          color: #64748b;
          line-height: 1.7;
        }

        .similar-list {
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
        }

        .similar-card {
          display: flex;
          gap: 1.25rem;
          text-decoration: none;
          color: inherit;
          background: #f8fafc;
          border-radius: 15px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .similar-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
          border-color: #dc3545;
        }

        .similar-img-box {
          width: 130px;
          height: 100px;
          flex-shrink: 0;
          position: relative;
        }

        .similar-img-box img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .similar-category {
          position: absolute;
          top: 0.5rem;
          left: 0.5rem;
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          padding: 0.3rem 0.9rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 800;
        }

        .similar-text-box {
          padding: 1rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .similar-title-small {
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }

        .similar-date-small {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .newsletter-description {
          font-size: 0.95rem;
          color: #64748b;
          line-height: 1.7;
          margin-bottom: 1.75rem;
        }

        .form-newsletter {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .input-newsletter {
          width: 100%;
          padding: 1rem 1.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 35px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          outline: none;
        }

        .input-newsletter:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.1);
        }

        .btn-newsletter {
          width: 100%;
          padding: 1rem 1.75rem;
          background: linear-gradient(135deg, #dc3545, #c82333);
          color: white;
          border: none;
          border-radius: 35px;
          font-size: 1rem;
          font-weight: 800;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(220, 53, 69, 0.3);
        }

        .btn-newsletter:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 28px rgba(220, 53, 69, 0.5);
        }

        .links-list-custom {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .links-list-custom li {
          margin-bottom: 1.25rem;
        }

        .links-list-custom a {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-decoration: none;
          color: #0f172a;
          font-size: 1rem;
          font-weight: 700;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 10px;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .links-list-custom a:hover {
          background: linear-gradient(135deg, #dc3545, #198754);
          color: white;
          transform: translateX(8px);
          border-color: #dc3545;
        }

        .arrow-link {
          font-size: 1.4rem;
          font-weight: 800;
          transition: transform 0.3s ease;
        }

        .links-list-custom a:hover .arrow-link {
          transform: translateX(8px);
        }

        /* Responsive */
        @media (max-width: 992px) {
          .article-main-title {
            font-size: 2.5rem;
          }

          .main-heading {
            font-size: 1.9rem;
          }

          .numbered-section {
            padding-left: 0;
          }

          .section-number {
            position: relative;
            display: block;
            margin-bottom: 1rem;
          }
        }

        @media (max-width: 768px) {
          .blog-wrapper {
            padding: 2rem 0 4rem;
          }

          .article-main-title {
            font-size: 2rem;
          }

          .article-top,
          .content-body,
          .article-bottom {
            padding: 2rem;
          }

          .excerpt-container {
            padding: 2rem;
            flex-direction: column;
            text-align: center;
          }

          .hero-image-container {
            height: 350px;
          }

          .meta-info-row {
            flex-direction: column;
            gap: 1rem;
          }

          .main-heading {
            font-size: 1.6rem;
          }

          .comparison-grid {
            grid-template-columns: 1fr;
          }

          .timeline-container::before {
            left: 15px;
          }

          .timeline-item {
            padding-left: 60px;
          }

          .marker-number {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }

          .consequences-grid {
            grid-template-columns: 1fr;
          }

          .pillars-grid {
            grid-template-columns: 1fr;
          }

          .assistance-timeline {
            grid-template-columns: 1fr;
          }

          .final-cta-section {
            padding: 2.5rem 2rem;
          }

          .final-cta-section h3 {
            font-size: 1.5rem;
          }

          .cta-subtitle {
            font-size: 1.2rem;
          }

          .similar-card {
            flex-direction: column;
          }

          .similar-img-box {
            width: 100%;
            height: 180px;
          }
        }

        @media (max-width: 576px) {
          .article-main-title {
            font-size: 1.75rem;
          }

          .badges-row {
            justify-content: center;
          }

          .social-buttons-row {
            justify-content: center;
          }

          .section-number {
            font-size: 3.5rem;
          }
        }
      `}</style>
      <Footer />
    </>
  );
};

export default DetailBlog8;